/* Pia › Einstellungen › „Pias Stimme“ (30.10.2026): wann Pia vorliest, welche Stimme, wie schnell. Alles je Gerät; gesprochen wird
   nur mit den Stimmen des Geräts (lib/piaStimme), der Text verlässt das Gerät nicht. */
import { useState } from 'react';
import { Play, Square } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import * as stimme from '@/lib/piaStimme';

const PROBE = 'Hi, ich bin Pia. Um 17 Uhr kommt Lena zum Probetraining Bang Muay Thai. Sie hat sich über Instagram gemeldet und möchte vor allem fitter werden.';

function Hinweis() {
  const ios = /iPhone|iPad|iPod/.test(navigator.userAgent);
  const mac = /Macintosh/.test(navigator.userAgent) && !ios;
  if (ios) return <p className="text-xs text-muted-foreground">Natürlicher klingt Pia mit einer geladenen Stimme: Einstellungen › Bedienungshilfen › Gesprochene Inhalte › Stimmen › Deutsch, dort „Anna“ oder „Petra“ in der Variante „Erweitert“ oder „Premium“ laden. Danach diese Seite neu laden und die Stimme hier wählen.</p>;
  if (mac) return <p className="text-xs text-muted-foreground">Natürlicher klingt Pia mit einer geladenen Stimme: Systemeinstellungen › Bedienungshilfen › Gesprochene Inhalte › Systemstimme › Stimmen verwalten › Deutsch, dort „Anna“ oder „Petra“ (Premium oder Erweitert) laden. Danach den Browser neu starten und die Stimme hier wählen.</p>;
  return <p className="text-xs text-muted-foreground">Welche Stimmen es gibt, hängt vom Gerät ab. Online-Stimmen des Browsers sind bewusst nicht dabei: Sie würden den Text an Microsoft oder Google schicken.</p>;
}

export default function PiaStimme() {
  const stimmen = stimme.useStimmen();
  const sprichtId = stimme.useSprichtGerade();
  const [modus, setModus] = useState(stimme.modus);
  const [uri, setUri] = useState(stimme.gewaehlteUri);
  const [tempo, setTempo] = useState(stimme.tempo);

  if (!stimme.verfuegbar()) {
    return <Card><CardContent className="p-4 space-y-1"><h2 className="text-sm font-bold">Pias Stimme</h2>
      <p className="text-sm text-muted-foreground">Dieser Browser kann nicht vorlesen.</p></CardContent></Card>;
  }
  const aktiv = stimmen.find((v) => v.voiceURI === uri) || stimmen[0] || null;
  const probe = () => (sprichtId === 'probe' ? stimme.stoppen() : stimme.vorlesen(PROBE, 'probe', { stimme: aktiv, rate: tempo }));

  return (
    <Card><CardContent className="p-4 space-y-3" data-testid="pia-stimme">
      <h2 className="text-sm font-bold">Pias Stimme</h2>
      <p className="text-sm text-muted-foreground">Pia liest mit einer Stimme dieses Geräts vor. Der Text verlässt das Gerät dafür nicht. An jeder Nachricht von Pia ist ein Lautsprecher-Knopf.</p>

      <fieldset className="space-y-1.5">
        <legend className="text-xs font-semibold mb-1">Automatisch vorlesen</legend>
        {stimme.MODI.map(([k, titel, text]) => (
          <label key={k} className="flex items-start gap-2 text-sm cursor-pointer">
            <input type="radio" name="pia-vorlesen" className="mt-1 accent-marke" checked={modus === k} data-testid={`pia-vorlesen-${k}`}
              onChange={() => { setModus(k); stimme.modusSetzen(k); if (k === 'aus') stimme.stoppen(); }} />
            <span><span className="font-medium">{titel}</span> <span className="text-muted-foreground">· {text}</span></span>
          </label>
        ))}
      </fieldset>

      {stimmen.length === 0 ? (
        <p className="text-sm text-amber-600">Auf diesem Gerät ist keine deutsche Stimme installiert.</p>
      ) : (
        <div className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
          <label className="block text-xs font-semibold min-w-0">Stimme
            <select value={aktiv?.voiceURI || ''} data-testid="pia-stimme-wahl"
              onChange={(e) => { setUri(e.target.value); stimme.stimmeSetzen(e.target.value); stimme.stoppen(); }}
              className="mt-1 block w-full h-11 rounded-md border border-input bg-background px-2 text-sm font-normal">
              {stimmen.map((v, i) => (
                <option key={v.voiceURI} value={v.voiceURI}>{v.name}{i === 0 ? ' (empfohlen)' : ''}{stimme.istMaennlich(v) ? ' · männlich' : ''}</option>
              ))}
            </select>
          </label>
          <Button type="button" variant="outline" onClick={probe} className="gap-1.5 min-h-[44px]" data-testid="pia-stimme-probe">
            {sprichtId === 'probe' ? <Square className="w-4 h-4" /> : <Play className="w-4 h-4" />}{sprichtId === 'probe' ? 'Stopp' : 'Probe hören'}
          </Button>
        </div>
      )}

      <label className="block text-xs font-semibold">Tempo: {tempo === 1 ? 'normal' : tempo < 1 ? 'langsamer' : 'schneller'}
        <input type="range" min="0.8" max="1.2" step="0.05" value={tempo} data-testid="pia-stimme-tempo"
          onChange={(e) => { const t = parseFloat(e.target.value); setTempo(t); stimme.tempoSetzen(t); }}
          className="mt-1 block w-full accent-marke" />
      </label>
      <Hinweis />
    </CardContent></Card>
  );
}
